import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import EnumInput from '../../../../../misc/EnumInput';
import SchoolSoftFilterInput from '../SchoolSoftFilterInput';
import { CoreSchoolSoftScheduleConfiguration, SchoolSoftScheduleEventNaming, SchoolSoftScheduleSelectedEmail } from '../../../../../../models/CoreSchoolSoftScheduleConfigurationModels';
import * as SchoolSoftScheduleConfigurationTypes from './SchoolSoftScheduleConfigurationTypes';


export const validator = (configuration: CoreSchoolSoftScheduleConfiguration, active: boolean) => {
    const validation = [];

    if (active) {
        if (!configuration.webApp || configuration.webApp === "") {
            validation.push('webApp');
        }
        if (configuration.selectedEmail === SchoolSoftScheduleSelectedEmail.Undefined) {
            validation.push('selectedEmail');
        }
        if (configuration.eventNaming === SchoolSoftScheduleEventNaming.Undefined) {
            validation.push('eventNaming');
        }
    }
    return validation;
}


interface SchoolSoftScheduleConfigurationProps {
    configuration: CoreSchoolSoftScheduleConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreSchoolSoftScheduleConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}

const SchoolSoftScheduleConfiguration = (props: SchoolSoftScheduleConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.SchoolSoftSchedule.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <div style={{ margin: '1rem 0' }}>
                        <FormGroup>
                            <Label tag="h5" for="baseAddress">{t('schoolSoftSchedule.baseAddress.label', { ns: 'configuration' })}</Label>
                            <Input
                                type="text"
                                name="webApp"
                                id="webApp"
                                placeholder={t('schoolSoftSchedule.baseAddress.placeHolder', { ns: 'configuration' })}
                                value={props.configuration.webApp || ''}
                                disabled={props.disabled}
                                onChange={(e) => props.updatePart({ webApp: e.target.value })}
                                invalid={validation.indexOf('webApp') !== -1}
                                autoComplete="off"
                            />
                            <FormFeedback>{t('schoolSoftSchedule.baseAddress.formFeedback', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>

                        <EnumInput
                            title={t('schoolSoftSchedule.selectedEmail.title', { ns: 'configuration' })}
                            name='selectedEmail'
                            value={props.configuration.selectedEmail}
                            options={SchoolSoftScheduleConfigurationTypes.selectedEmails.map(x => ({ value: x, text: t(`schoolSoftSchedule.selectedEmail.options.${x}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('selectedEmail') !== -1}
                            invalidFeedback={t('schoolSoftSchedule.selectedEmail.formFeedback', { ns: 'configuration' })}
                        />

                        <EnumInput
                            title={t('schoolSoftSchedule.eventNaming.title', { ns: 'configuration' })}
                            name='eventNaming'
                            value={props.configuration.eventNaming}
                            options={SchoolSoftScheduleConfigurationTypes.scheduleEventNaming.map(x => ({ value: x, text: t(`schoolSoftSchedule.eventNaming.options.${x}`, { ns: 'configuration' }) }))}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            disabled={props.disabled}
                            invalid={validation.indexOf('eventNaming') !== -1}
                            invalidFeedback={t('schoolSoftSchedule.eventNaming.formFeedback', { ns: 'configuration' })}
                        />

                        <FormGroup>
                            <Label tag="h5"> {t('schoolSoftSchedule.eventFilters.title', { ns: 'configuration' })}</Label>
                            <p>{t('schoolSoftSchedule.eventFilters.description', { ns: 'configuration' })}</p>
                            <SchoolSoftFilterInput
                                name="eventFilters"
                                filters={props.configuration.eventFilters}
                                onChange={(name, value) => props.updatePart({ [name]: value })}
                                disabled={props.disabled}
                            />
                        </FormGroup>
                    </div>
                </CardBody>
            </Collapse>
        </Card>)
}

export default SchoolSoftScheduleConfiguration;
